/**
 * Webhook Setup Wizard — Jira / Linear connection helper.
 *
 * One-click wizard that:
 *   1. Provisions a signing secret (auto-generated, server-side)
 *   2. Shows the operator the exact webhook URL + signature header
 *   3. Renders label conventions (launch / finops-delta:* / cartie-customers:*)
 *   4. Generates a copy-pasteable Atlassian Automation / Linear webhook JSON
 *   5. Provides a "Send test payload" button that calls the sample-payload endpoint
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Copy, Webhook, KeyRound, Tags, Calendar, FileJson, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CopyButton = ({ value, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(typeof value === 'string' ? value : JSON.stringify(value, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  };
  return (
    <button onClick={onCopy} className="inline-flex items-center gap-1 text-xs text-cyan-300 hover:text-cyan-200 transition-colors" data-testid="ws-copy-btn">
      {copied ? <><CheckCircle2 className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> {label}</>}
    </button>
  );
};

export default function WebhookSetupWizard({ source = 'jira' }) {
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const provision = async () => {
    setBusy(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/roadmap-webhooks/setup-wizard?source=${source}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
      setConfig(await r.json());
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(false); }
  };

  const sourceLabel = source === 'jira' ? 'Jira' : 'Linear';
  const sourceColor = source === 'jira' ? 'from-sky-500 to-blue-500' : 'from-emerald-500 to-cyan-500';

  return (
    <Card className="bg-slate-900 border-cyan-500/20" data-testid={`webhook-wizard-${source}`}>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <div className={`inline-flex w-8 h-8 rounded-md bg-gradient-to-br ${sourceColor} items-center justify-center`}>
              <Webhook className="w-4 h-4 text-white" />
            </div>
            Connect {sourceLabel} (1-click setup)
          </CardTitle>
          {!config && (
            <Button onClick={provision} disabled={busy} className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid={`provision-${source}-btn`}>
              {busy ? 'Provisioning…' : `Set up ${sourceLabel}`}
            </Button>
          )}
        </div>
        {!config && <p className="text-xs text-slate-400 mt-2">Auto-generates a signing secret + a copy-pasteable {sourceLabel} {source === 'jira' ? 'Automation rule' : 'webhook config'}. No manual JSON-crafting required.</p>}
      </CardHeader>

      {error && <CardContent><div className="rounded-md border border-rose-500/30 bg-rose-500/10 text-rose-300 px-3 py-2 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {error}</div></CardContent>}

      {config && (
        <CardContent className="space-y-5">
          {/* Webhook URL + secret */}
          <div className="rounded-md bg-slate-950 border border-slate-700 p-3">
            <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-2 flex items-center gap-1.5"><KeyRound className="w-3 h-3" /> Step 1 · Credentials</div>
            <div className="space-y-2 text-xs">
              <div className="flex items-start justify-between gap-2">
                <div className="text-slate-400">Webhook URL</div>
                <div className="flex items-center gap-2">
                  <code className="font-mono text-emerald-300 break-all text-right" data-testid="ws-webhook-url">{config.webhook_url}</code>
                  <CopyButton value={config.webhook_url} />
                </div>
              </div>
              <div className="flex items-start justify-between gap-2">
                <div className="text-slate-400">Signature header</div>
                <code className="font-mono text-cyan-300">{config.signature_header}</code>
              </div>
              <div className="flex items-start justify-between gap-2">
                <div className="text-slate-400">Signing secret</div>
                <div className="flex items-center gap-2">
                  <code className="font-mono text-amber-300 break-all" data-testid="ws-secret">{config.signing_secret}</code>
                  <CopyButton value={config.signing_secret} />
                </div>
              </div>
              {config.secret_was_existing && (
                <div className="text-[10px] text-amber-400/80 italic">⚠️ Re-using existing secret. Hit POST /api/roadmap-webhooks/config to rotate.</div>
              )}
            </div>
          </div>

          {/* Label conventions */}
          <div className="rounded-md bg-slate-950 border border-slate-700 p-3">
            <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-2 flex items-center gap-1.5"><Tags className="w-3 h-3" /> Step 2 · Label conventions on your issues</div>
            <div className="space-y-2">
              {config.label_conventions.map((l, i) => (
                <div key={i} className="rounded border border-slate-800 bg-slate-900 px-3 py-2">
                  <div className="flex items-center gap-2 mb-0.5">
                    <code className="font-mono text-emerald-300 text-sm font-bold">{l.label}</code>
                    {l.format && <Badge className="bg-slate-800 border border-slate-700 text-slate-300 text-[9px] font-mono">{l.format}</Badge>}
                  </div>
                  <div className="text-xs text-slate-400">{l.what_it_does}</div>
                  {l.aliases && <div className="text-[10px] text-slate-500 mt-0.5">Aliases: {l.aliases.join(', ')}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Date field */}
          <div className="rounded-md bg-slate-950 border border-slate-700 p-3">
            <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-2 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Step 3 · Date field used for forecasting</div>
            <code className="font-mono text-cyan-300 text-xs">{config.date_field_used}</code>
            <p className="text-xs text-slate-200 mt-1">CARTIE injects the event at this date as a step in the Monte Carlo forecast fan.</p>
          </div>

          {/* Generated automation snippet */}
          <div className="rounded-md bg-slate-950 border border-slate-700 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest flex items-center gap-1.5"><FileJson className="w-3 h-3" /> Step 4 · Paste into {sourceLabel} {source === 'jira' ? 'Automation' : 'Webhooks'}</div>
              <CopyButton value={config.automation_snippet} label="Copy JSON" />
            </div>
            <pre className="text-[10px] text-slate-300 bg-slate-950 p-3 rounded overflow-x-auto max-h-64" data-testid="ws-automation-json">{JSON.stringify(config.automation_snippet, null, 2)}</pre>
          </div>

          {/* Operator instructions */}
          <div className="rounded-md bg-cyan-500/5 border border-cyan-500/20 p-3">
            <div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-2">Step 5 · Walk-through</div>
            <ol className="space-y-1.5 text-xs text-slate-300">
              {config.operator_instructions.map((step, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-cyan-400 shrink-0">{i + 1}.</span>
                  <span>{step.replace(/^\d+\.\s*/, '')}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Verify */}
          <div className="rounded-md bg-emerald-500/5 border border-emerald-500/20 p-3">
            <div className="text-[10px] font-bold text-emerald-300 uppercase tracking-widest mb-2">Step 6 · Verify the wiring</div>
            <p className="text-xs text-slate-300 mb-2">Once your {sourceLabel} automation is live, tag any issue with <code className="text-emerald-300">launch</code> + an impact label and update it. The event should appear in <code className="text-emerald-300">/admin/roadmap-forecast</code> within seconds.</p>
            <a href={config.test_endpoint} target="_blank" rel="noreferrer" className="text-xs text-emerald-300 hover:text-emerald-200">→ View sample payload</a>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
